/** @format */

import useModal from '@/components/shared/Modal';
import ModalDialog from '@/components/shared/Modal/partials/Dialog';
import { selectorBookingCommercialConditions } from '@/ducks/bookingNew/selectors';
import { GTMPDPShowAllServices } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '@/ducks/room/declarations';
import { useRoomServices } from '@/ducks/room/hooks/useRoomServices';
import { TTranslation } from '@/i18n/createProvider';
import { useAppTranslation } from '@/i18n/declarations';
import { useAppSelector } from '@/redux/hooks';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { FormattedMessage } from 'gatsby-plugin-intl';
import React, { memo, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CustomTitle } from '../index.style';

interface Props {
  room: Omit<
    TRoom,
    | 'accomodationType'
    | 'countryCode'
    | 'flat'
    | 'legalEntity'
    | 'id'
    | 'salableOnline'
    | 'dateDifference'
    | 'resultType'
    | 'bedroomAvailableCount'
  >;
  coliversCount: number;
}

export const RoomWhatYouWillFind = memo(({ coliversCount, room }: Props) => {
  const { t } = useAppTranslation();
  const { Modal } = useModal();
  const [modalOpen, setModalOpen] = useState(false);

  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);

  const modalRef = useRef<HTMLDivElement>(null);

  const { RoomFeatures, FlatFeatures } = useRoomServices(room as TRoom, coliversCount);

  const handleGTMPDPShowAllServices = (id?: string) => {
    const { getFormattedPrice } = utilityGetPrice({ commercialConditions, room: room as TRoom });
    GTMPDPShowAllServices(room.code, getFormattedPrice());
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setModalOpen(false);
    }
  };

  useEffect(() => {
    // Add the event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <CustomTitle $subtitle={true}>
        <FormattedMessage id="pdp.what_you_will_find" />
      </CustomTitle>

      <div>
        <CustomSubTitle dangerouslySetInnerHTML={{ __html: t('pdp.in_your_room' as TTranslation) }} />
        <Features>
          {RoomFeatures.map((feature, index) =>
            feature.valid && feature.priority ? (
              <Feature key={feature.text + index.toString()}>
                <FeatureImageWrapper>
                  <img src={feature.image} alt={feature.name} />
                </FeatureImageWrapper>
                {feature.text}
              </Feature>
            ) : (
              ''
            ),
          )}
        </Features>
      </div>
      <CustomSubTitle dangerouslySetInnerHTML={{ __html: t('pdp.in_your_flat' as TTranslation) }} />
      <div>
        <Features>
          {FlatFeatures.map((feature, index) =>
            feature.valid && feature.priority ? (
              <Feature key={feature.text + index.toString()}>
                <FeatureImageWrapper>
                  <img src={feature.image} alt={feature.name} />
                </FeatureImageWrapper>
                {feature.text}
              </Feature>
            ) : (
              ''
            ),
          )}
        </Features>
      </div>

      <ShowMoreButton
        onClick={() => {
          handleGTMPDPShowAllServices();
          setModalOpen(true);
        }}
      >
        <FormattedMessage id="pdp.show_all_services" />
      </ShowMoreButton>

      <Modal isShow={modalOpen} opacity={0.5} onClose={() => setModalOpen(false)}>
        <ModalDialog title="pdp.what_you_will_find" setModalOpen={setModalOpen}>
          <FeaturesModalWrapperWrap>
            <FeaturesModalWrapperWrapOverflower>
              <CustomSubTitle dangerouslySetInnerHTML={{ __html: t('pdp.in_your_room' as TTranslation) }} />
              <Features>
                {RoomFeatures.map((feature, index) =>
                  feature.valid ? (
                    <Feature key={feature.text + index.toString()}>
                      <FeatureImageWrapper>
                        <img src={feature.image} alt={feature.name} />
                      </FeatureImageWrapper>
                      {feature.text}
                    </Feature>
                  ) : (
                    ''
                  ),
                )}
              </Features>
              <CustomSubTitle dangerouslySetInnerHTML={{ __html: t('pdp.in_your_flat' as TTranslation) }} />
              <Features>
                {FlatFeatures.map((feature, index) =>
                  feature.valid ? (
                    <Feature key={feature.text.key + index.toString()}>
                      <FeatureImageWrapper>
                        <img src={feature.image} alt={feature.name} />
                      </FeatureImageWrapper>
                      {feature.text}
                    </Feature>
                  ) : (
                    ''
                  ),
                )}
              </Features>
            </FeaturesModalWrapperWrapOverflower>
          </FeaturesModalWrapperWrap>
        </ModalDialog>
      </Modal>
    </>
  );
});

const FeaturesModalWrapperWrapOverflower = styled.div`
  margin-bottom: 24px;
`;

const FeaturesModalWrapperWrap = styled.div`
  height: 100%;
  overflow-y: auto;
`;

const ShowMoreButton = styled.button`
  --accent-color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  text-decoration: none;

  padding: 0.5rem 1rem;
  color: ${({ theme }) => theme.colors.joivy.white};
  background-color: var(--accent-color);
  border: 1px solid var(--accent-color);
  border-radius: 2.25rem;
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: clamp(0.03rem, 0.0287rem + 0.0056vw, 0.03375rem);
  cursor: pointer;

  transition: all 0.5s cubic-bezier(0.36, 0.33, 0, 1) 0s;

  &:hover {
    --accent-color: ${({ theme }) => theme.colors.joivy['teal-light']};
  }

  margin-top: 0.75rem;
`;

const CustomSubTitle = styled.h3`
  font-family: ${({ theme }) => theme.font.body};
  margin-block: 0 0.5rem;

  font-size: clamp(1rem, 0.9583rem + 0.1852vw, 1.125rem);
  line-height: clamp(1.375rem, 1.3333rem + 0.1852vw, 1.5rem);
  letter-spacing: clamp(0.03rem, 0.0287rem + 0.0056vw, 0.03375rem);

  color: ${({ theme }) => theme.colors.joivy.pink};

  span {
    font-style: normal;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const FeatureImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Feature = styled.div`
  display: grid;
  grid-template-columns: 44px auto;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-start;
  width: 50%;

  border-radius: 0.94rem;
  max-height: 4.375rem;

  @media screen and (max-width: ${props => props.theme.breakpoints.xs}) {
    width: 100%;
  }
  p {
    color: ${({ theme }) => theme.colors.joivy.black};
    font-size: clamp(1rem, 0.9583rem + 0.1852vw, 1.125rem);
    line-height: clamp(1.375rem, 1.3333rem + 0.1852vw, 1.5rem);
    letter-spacing: clamp(0.03rem, 0.0287rem + 0.0056vw, 0.03375rem);
  }
`;

const Features = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

export default RoomWhatYouWillFind;
